import React, { useEffect, useRef, useState } from 'react';

import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { filter, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { colorOpacity, customTheme } from 'theme';

import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { PrimaryIconButton } from 'components/DataEntry';
import { CustomDayPicker } from 'components/DataEntry/Date';
import {
  BreadcrumbListOptions,
  Card,
  ChartMixedIcon,
  ClockIcon,
  formatBytes,
  getIconColor,
  Select,
  StackedCell,
  StackedHeader,
  Tag,
} from 'components/index';
import { Breadcrumbs } from 'components/Navigation/Breadcrumbs';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { selectIncidentResponse } from 'containers/Visibility/IncidentResponse/selectors';
import { actions } from 'containers/Visibility/IncidentResponse/slice';
import { useSearchParams } from 'hooks/useSearchParams';
import { limitedString } from 'utils/string';

import {
  insightCategoryOptions,
  renderCloudEventsInsightsForResource,
  renderSourceIP,
} from '../../utils';
import { HourlyAccessSummary } from '../Summary/HourlyAccessSummary';

export const CloudEventsForResource = () => {
  const params = useSearchParams<{
    tab: string;
    resource_type: string;
    date: string;
    hour: string;
    total: string;
    insightCategories: string;
    insightCategoriesForResource: string;
    contexts: string;
    groupName: string;
  }>();
  const { group_id, resource_id } = useParams();
  const { getResourceAlias } = useResourceType();
  const navigate = useNavigate();
  const { resourceEvents } = useSelector(selectIncidentResponse);
  const { resourceInsight } = useSelector(selectSharedState);
  const dispatch = useDispatch();
  const { accountMapping } = useCloudAccountId();
  const summaryDisclosure = useDisclosure();
  const [insightCategories, setInsightCategories] = useState<
    Record<string, any>
  >([]);
  const { updateValueInURL } = useSearchParamFunctions();
  const pageNumber = resourceEvents.page.info.page_number;
  const pageSize = resourceEvents.page.info.page_size;
  const queryKey = JSON.stringify([
    params.hour,
    insightCategories.map(o => o?.value),
  ]);
  const previousQueryKey = useRef(queryKey);

  //set insight categories
  useEffect(() => {
    setInsightCategories(
      !!params.insightCategoriesForResource
        ? filter(insightCategoryOptions, o =>
            params?.insightCategoriesForResource?.includes(o.value),
          )
        : [],
    );
  }, [params.insightCategoriesForResource]);

  useEffect(() => {
    dispatch(
      sharedStateActions.getResourceInsight({
        q: {
          insightV2Request: {
            resource_type: params.resource_type,
            resource_ids: [resource_id!],
          },
        },
      }),
    );
  }, [params.resource_type, resource_id, dispatch]);

  useEffect(() => {
    const queryChanged = previousQueryKey.current !== queryKey;
    if (queryChanged) {
      previousQueryKey.current = queryKey;
      if (pageNumber !== 1) {
        dispatch(
          actions.updateResourceEventsPagination({
            page_number: 1,
            page_size: pageSize,
          }),
        );
        return;
      }
    }

    if (!isEmpty(resourceInsight.data) && !isEmpty(accountMapping))
      dispatch(
        actions.getResourceEvents({
          q: {
            body: {
              account_id:
                accountMapping[resourceInsight.data?.account]
                  ?.cloud_account_id || undefined,
              start: params.hour, //it is already in utc
              end: dayjs(params.hour).add(1, 'h').format().toString(),
              resource_arn: resourceInsight.data?.arn,
              region: resourceInsight?.data?.region,
              limit: pageSize,
              offset: (pageNumber - 1) * pageSize,
              insight_categories: insightCategories?.map(o => o?.value),
            },
          },
        }),
      );
  }, [
    dispatch,
    params.hour,
    resourceInsight.data,
    accountMapping,
    pageNumber,
    pageSize,
    queryKey,
    insightCategories,
  ]);

  const breadcrumbList: BreadcrumbListOptions[] = [
    {
      label: 'Data',
      onClick: () => navigate('/visibility/activity?tab=Data'),
    },
    {
      label: 'Activity Group - ' + params?.groupName ?? '-',
      onClick: () =>
        navigate(
          '/visibility/activity/group/' +
            group_id +
            '?tab=Data&resource_type=' +
            params.resource_type +
            '&groupName=' +
            params.groupName +
            '&date=' +
            params.date +
            '&insightCategories=' +
            params.insightCategories +
            (params.contexts
              ? '&contexts=' + encodeURIComponent(params.contexts)
              : ''),
        ),
    },
    {
      label: resource_id + ' - Aggregated',
      onClick: () =>
        navigate(
          '/visibility/activity/group/' +
            group_id +
            '/resource/' +
            resource_id +
            '?tab=Data&resource_type=' +
            params.resource_type +
            '&groupName=' +
            params.groupName +
            '&date=' +
            params.date +
            '&insightCategories=' +
            params.insightCategories +
            (params.contexts
              ? '&contexts=' + encodeURIComponent(params.contexts)
              : ''),
        ),
    },
    { label: resource_id + ' - Events' },
  ];

  const columns: TableColumnProps[] = [
    {
      header: 'Time',
      render: ({ row }) => {
        const time = dayjs(row.time).format('MMM DD, hh:mm:ss A');
        return time;
      },
    },
    {
      header: 'Event',
      render: ({ row }) => (
        <StackedCell upper={row?.action} lower={row?.object_path} />
      ),
      align: 'left',
      styles: { width: '250px', cell: { maxWidth: '250px' } },
    },
    {
      header: 'Status',
      accessor: '',
      render: ({ row }) =>
        row?.failed ? (
          <Tag
            label="Failed"
            styles={{
              tag: {
                bg: colorOpacity(customTheme.colors.high, 0.1),
                border: 'none',
                borderRadius: 12,
                py: 0.5,
              },
              label: { color: customTheme.colors.high },
            }}
          />
        ) : (
          ''
        ),
    },
    {
      header: 'Requester',
      render: ({ row }) => (
        <StackedCell
          upper={row?.requester_name || '-'}
          lower={getResourceAlias(row?.requester_type) || row?.requester_type}
        />
      ),
      styles: { width: '200px', cell: { maxWidth: '200px' } },
      align: 'left',
    },
    {
      header: 'Mechanism',
      accessor: 'mechanism',
      align: 'left',
      render: ({ row }) => {
        console.log();
        return (
          <StackedCell
            lower={getResourceAlias(row?.mechanism) || '-'}
            upper={row?.mechanism_value || '-'}
          />
        );
      },
      styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
    {
      header: 'Source IP',
      render: renderSourceIP,
      align: 'left',
    },
    {
      header: 'Data Size',
      accessor: 'data_size_bytes',
      render: ({ value }) => formatBytes(value),
    },
    {
      header: 'Insights',
      render: renderCloudEventsInsightsForResource,
    },
  ];

  return (
    <Card>
      <Stack w="full" h="full" spacing={4}>
        <Breadcrumbs list={breadcrumbList} />
        <HStack w="full" justify="space-between">
          <Box flex={1}>
            <StackedHeader
              upper={'Cloud Events for ' + resource_id}
              lower={getResourceAlias(params.resource_type)}
              type={params.resource_type}
              color={getIconColor('aws')}
              useCustomColor
              border
              borderColor={customTheme.colors.yellow['300']}
            />
          </Box>
          <PrimaryIconButton
            aria-label="hourly-access-summary"
            icon={<ChartMixedIcon />}
            tooltip={
              'Hourly access summary for ' + limitedString(resource_id, 16)
            }
            tooltipW={32}
            color={customTheme.colors.gray['300']}
            bg={customTheme.colors.gray['50']}
            borderColor={customTheme.colors.gray['200']}
            size="md"
            onClick={summaryDisclosure.onOpen}
            _hover={{ bg: colorOpacity(customTheme.colors.primary, 0.2) }}
            _active={{ bg: colorOpacity(customTheme.colors.primary, 0.2) }}
            _focus={{ bg: colorOpacity(customTheme.colors.primary, 0.2) }}
          />
        </HStack>
        <BorderedBox flex={1} w="full" h="full" p={4} minH="500px">
          <Stack w="full" h="full" spacing={4}>
            <HStack>
              <FilterLabelWrapper>VIEW BY</FilterLabelWrapper>
              <Box pl={2} color={customTheme.colors.gray['300']}>
                Date
              </Box>
              <Box>
                <CustomDayPicker
                  isDisabled
                  dateProps={{
                    mode: 'single' as any,
                    selected: params.date,
                  }}
                />
              </Box>
              <Box pl={2} color={customTheme.colors.gray['300']}>
                Hour
              </Box>
              <BorderedBox
                py={0}
                color={customTheme.colors.gray['200']}
                cursor="not-allowed"
              >
                <HStack>
                  <Center boxSize={5} color={customTheme.colors.gray['250']}>
                    <Center>
                      <ClockIcon />
                    </Center>
                  </Center>
                  <Box py="5px">
                    {dayjs(params.hour).local().format('HH:mm:ss') +
                      ' - ' +
                      dayjs(params.hour).add(1, 'h').local().format('HH:mm:ss')}
                  </Box>
                </HStack>
              </BorderedBox>
              <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
              <Box>
                <Select
                  leftMessage="Insight"
                  isAFilter
                  isMulti
                  options={insightCategoryOptions}
                  value={insightCategories}
                  onChange={s =>
                    updateValueInURL(
                      'insightCategoriesForResource',
                      s?.map(o => o?.value),
                    )
                  }
                  menuPortalTarget={document.body}
                />
              </Box>
            </HStack>
            <Box flex={1} w="full" h="full">
              <Table
                columns={columns}
                data={resourceEvents.data}
                isLoading={
                  resourceEvents.isLoading || resourceInsight.isLoading
                }
                pagination={{
                  pageInfo: resourceEvents.page.info,
                  onChange: info =>
                    dispatch(actions.updateResourceEventsPagination(info)),
                  totalCount: parseInt(params.total),
                  pageSizes: [100, 200, 500, 800, 1000],
                }}
              />
            </Box>
          </Stack>
          {summaryDisclosure.isOpen && (
            <HourlyAccessSummary
              accountId={
                accountMapping[resourceInsight.data?.account]?.cloud_account_id
              }
              date={params.date}
              isOpen={summaryDisclosure.isOpen}
              onClose={summaryDisclosure.onClose}
              resourceArn={resourceInsight.data?.arn}
              resourceName={resource_id ?? ''}
              resourceType={params.resource_type}
            />
          )}
        </BorderedBox>
      </Stack>
    </Card>
  );
};
